import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Card } from '../../components/ui/Card';
import { Wordmark } from '../../components/ui/Wordmark';
import { brand } from '../../config/brand';

/**
 * The narrow centred frame every auth screen shares.
 *
 * ## Two ways out, on purpose
 *
 * These are the only screens in the app with no header and no dock - see
 * `BARE` in `AppShell`, where the argument is that a header offering Discover
 * is an invitation to abandon the form. That argument holds and it had a hole
 * in it: somebody who lands here from a link, or clicks Sign in to see what
 * signing in involves, had no way back to the site at all. Not a nav bar, not a
 * logo, nothing. The only exits were the browser's back button and the form.
 *
 * So the lockup is a link home, which is where anybody who has used a website
 * before will reach first, and there is a plain one underneath the card for
 * anybody who does not think to try. Two exits on a page whose whole problem
 * was having none is not redundancy worth trimming.
 *
 * Both point at `/`, which is correct in either state: a stranger gets the
 * landing page and somebody already signed in - which `verify-email` and
 * `reset-password` both allow - gets their dashboard.
 *
 * ## The lockup is drawn, not loaded
 *
 * It was an `<img>` of `mark.svg`, which bakes its colours in - including the
 * `#151530` tile behind the bars, a near-black that sat as a dark square on the
 * light theme while every other instance of the mark in the app followed the
 * theme. `Wordmark` draws the same shape from tokens. It also spells the name,
 * which the mark alone did not, and these are the pages where a visitor is most
 * likely to be meeting it for the first time.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="mx-auto flex max-w-md flex-col gap-6 px-6 py-16">
      <div className="flex flex-col items-center gap-4 text-center">
        <Link
          to="/"
          data-auth-home
          aria-label={`${brand.name} home`}
          className="rounded-[var(--radius-chip)] transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent"
        >
          <Wordmark className="h-9 w-auto" />
        </Link>

        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-soft">{subtitle}</p>}
      </div>

      <Card className="p-6">{children}</Card>

      {footer && <div className="text-center text-sm text-soft">{footer}</div>}

      {/*
        Quiet, and last. It is the way out rather than the thing to do here, so
        it sits below the reason somebody came and is styled to be found when
        looked for rather than to compete with the form.
      */}
      <div className="text-center">
        <Link
          to="/"
          className="font-mono text-[11px] text-faint transition-colors hover:text-ink"
        >
          ← Back to {brand.wordmark}
        </Link>
      </div>
    </div>
  );
}
